import {
  all, delay, put, select, spawn, takeEvery, takeLeading
} from "redux-saga/effects";

import { STORAGE_KEYS, save, get } from "API/LocalStorage";
import {
  projectsAdd,
  projectsLastRequestIdListSet,
  projectsLastRequestOnSet,
  projectsRetrieveAutomatic,
  projectsRetrieveSinceError,
  projectsRetrieveSinceStart,
  projectsRetrieveSinceSuccess,
  uiRefreshEveryTimeLoad,
  uiRefreshEveryTimeSet
} from "Store/Actions";
import { PayloadAction } from "@reduxjs/toolkit";
import { selectAllowAutoRefresh, selectRefreshEveryTime } from "Store/Selectors/UI";
import { selectLastRequestIdList, selectLastRequestOn } from "Store/Selectors/Projects";
import { getProjectsSince } from "API/Project";
import { ProjectCookie } from "Utils/ProjectCookie";
import { IProjectsResponse } from "Model/Responses/IProjectsResponse";
import { hasElements } from "Utils/Array";
import { callWithAuth } from "./CallWithAuth";
import { sortAndFilterSaga } from "./Projects";

function automaticRefreshSetSaga(action: PayloadAction<number>) {
  const { payload } = action;
  save(STORAGE_KEYS.REFRESH_EVERY_TIME, payload);
}

function* loadAutomaticRefreshSaga() {
  const refreshEveryTime = get<number>(STORAGE_KEYS.REFRESH_EVERY_TIME) ?? 60;
  yield put(uiRefreshEveryTimeSet(refreshEveryTime));
}

function* projectsRetrieveAutomaticSaga() {
  const timeInSeconds: number = yield select(selectRefreshEveryTime);
  yield delay(timeInSeconds * 1000);
  yield spawn(projectsRetrieveSinceStartSaga, projectsRetrieveSinceStart(false));
}

function* projectsRetrieveSinceStartSaga({ payload }: PayloadAction<boolean>) {
  const isManual = payload ?? false;
  const allowAutoRefresh: boolean = yield select(selectAllowAutoRefresh);
  if (!allowAutoRefresh && !isManual) {
    yield put(projectsRetrieveAutomatic());
    return;
  }

  const [since, idList] = yield all([select(selectLastRequestOn), select(selectLastRequestIdList)]);
  if (since == null) {
    return;
  }

  try {
    const response: IProjectsResponse = yield callWithAuth(
      getProjectsSince,
      ProjectCookie.fromStore(since, idList).sinceCookie
    );

    const projectCookie = new ProjectCookie(response.cookie);
    const hasNewProjects = hasElements(response.projects);

    if (hasNewProjects) {
      yield put(projectsAdd(response.projects));
    }

    yield all([
      hasNewProjects ? spawn(sortAndFilterSaga) : null,
      put(projectsLastRequestOnSet(projectCookie.requestOn)),
      put(projectsLastRequestIdListSet(projectCookie.idList)),
      put(projectsRetrieveSinceSuccess())
    ]);
  } catch (e) {
    console.error(e);
    yield put(projectsRetrieveSinceError(e?.message ?? `Unknown error refreshing projects...`));
  }
}

export function* AutomaticRefresh(): unknown {
  yield all([
    takeEvery(uiRefreshEveryTimeLoad, loadAutomaticRefreshSaga),
    takeEvery(uiRefreshEveryTimeSet, automaticRefreshSetSaga),
    takeLeading([projectsRetrieveAutomatic, projectsRetrieveSinceSuccess], projectsRetrieveAutomaticSaga),
    takeLeading(projectsRetrieveSinceStart, projectsRetrieveSinceStartSaga)
  ]);
}
